import React, { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { UserContext } from './userContext';
import { AccountHeader, DetailRow, Icon } from './myacc/primitives';

/* ─────────── helpers ─────────── */

function roleLabel(user) {
  if (!user) return '-';
  if (user.profile_done !== '1') return 'Profile setup incomplete';
  if (user.user_role === '1') return 'Regular user';
  return user.cat_name || 'Member';
}

function getCallout(user) {
  if (user.profile_done === '0') {
    return {
      tone: 'amber',
      icon: <Icon.CheckCircle className="h-5 w-5" />,
      title: 'Complete your profile',
      body: 'Finish setting up your profile to unlock full access to the directory, bookmarks, and personalised listings.',
      cta: { to: '/myaccount/complete_profile', label: 'Complete now' },
    };
  }
  if (user.user_role === '1' && user.profile_done === '1') {
    return {
      tone: 'amber',
      icon: <Icon.ArrowUp className="h-5 w-5" />,
      title: 'Upgrade your profile',
      body: 'Become a Contractor, Consultant, Supplier, Engineer, Workforce, or Testing Lab provider to list your services and accept contact requests.',
      cta: { to: '/myaccount/upgradeprofile', label: 'Upgrade profile' },
    };
  }
  if (user.user_role === '2' && user.category_ids !== '6' && user.category_ids !== '7' && user.profile_done === '1') {
    return {
      tone: 'amber',
      icon: <Icon.Info className="h-5 w-5" />,
      title: 'Next steps',
      body: 'Add a product or service from "My Products / Services". Once it’s verified, choose a subscription plan from "My Subscription" so customers can contact you.',
      cta: { to: '/myaccount/my_prods_servs', label: 'Manage products' },
    };
  }
  if (user.user_role === '2' && (user.category_ids === '6' || user.category_ids === '7') && user.profile_done === '1') {
    return {
      tone: 'amber',
      icon: <Icon.Eye2 className="h-5 w-5" />,
      title: 'Control your visibility',
      body: 'Change whether your profile is visible to the public from Update Details.',
      cta: { to: '/myaccount/updatedetails', label: 'Update details' },
    };
  }
  return null;
}

/* ─────────── component ─────────── */

export const MyProfile = () => {
  const { user } = useContext(UserContext);
  if (!user) return null;

  const initial = (user.name || user.fname || '?').trim().charAt(0).toUpperCase();
  const callout = getCallout(user);

  return (
    <>
      <AccountHeader
        kicker="Account"
        title="My profile"
        subtitle="Your account overview and next steps."
      />

      {/* Identity card */}
      <section className="relative isolate overflow-hidden rounded-2xl border border-border-subtle bg-surface-paper p-5 sm:p-6 mb-4">
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -left-16 h-44 w-44 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.10), transparent 70%)' }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative shrink-0 mx-auto sm:mx-0">
            <span aria-hidden className="absolute -inset-1 rounded-2xl border border-amber/24 opacity-60" />
            <div className="relative flex h-[88px] w-[88px] sm:h-[96px] sm:w-[96px] items-center justify-center rounded-2xl border border-amber/40 bg-gradient-to-br from-amber/30 to-amber/8 text-amber font-extrabold text-[36px] sm:text-[40px] tracking-[-0.02em] shadow-[0_8px_24px_-10px_rgba(251,191,36,0.45)]">
              {initial}
            </div>
            {user.profile_done === '1' && (
              <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-amber border-2 border-surface-paper">
                <Icon.Check className="h-3 w-3 text-ink" />
              </div>
            )}
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <h2 className="text-[19px] sm:text-[22px] font-bold tracking-[-0.015em] text-fg-primary">
              {user.name || user.fname || 'User'}
            </h2>
            <div className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-0.5">
              <span aria-hidden className="inline-flex h-1 w-1 rounded-full bg-amber" />
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">{roleLabel(user)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Account details */}
      <section className="rounded-2xl border border-border-subtle bg-surface-paper p-5 sm:p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <span aria-hidden className="inline-flex h-1 w-1 rounded-full bg-amber" />
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">Account details</span>
        </div>
        <DetailRow
          label={<span className="inline-flex items-center gap-1.5"><Icon.User className="h-3 w-3" /> Name</span>}
          value={user.name || '-'}
        />
        <DetailRow
          label={<span className="inline-flex items-center gap-1.5"><Icon.Phone className="h-3 w-3" /> Mobile</span>}
          value={user.user_mobile || '-'}
        />
        {user.profile_done === '1' && (
          <DetailRow
            label={<span className="inline-flex items-center gap-1.5"><Icon.Tag className="h-3 w-3" /> Category</span>}
            value={user.user_role === '1' ? 'Regular user' : user.cat_name}
          />
        )}
        {user.user_email && (
          <DetailRow
            label={<span className="inline-flex items-center gap-1.5"><Icon.Mail className="h-3 w-3" /> Email</span>}
            value={user.user_email}
          />
        )}
      </section>

      {/* Conditional next-step callout */}
      {callout && (
        <section
          className="
            relative overflow-hidden rounded-2xl border border-amber/24 bg-amber/5 p-5
            flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4
          "
        >
          <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-amber" />
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber/15 text-amber">
            {callout.icon}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[14px] font-bold text-fg-primary">{callout.title}</h3>
            <p className="mt-1 text-[12.5px] leading-[1.6] text-fg-secondary">{callout.body}</p>
            {callout.cta && (
              <RouterLink
                to={callout.cta.to}
                className="
                  mt-3 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-3.5
                  text-[11.5px] font-bold uppercase tracking-[0.1em]
                  bg-amber text-ink
                  transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
                  hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                  active:scale-[0.98]
                "
              >
                {callout.cta.label}
                <Icon.ArrowRight className="h-3.5 w-3.5" />
              </RouterLink>
            )}
          </div>
        </section>
      )}
    </>
  );
};
